import React, {
  cloneElement,
  createContext,
  forwardRef,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  chakra,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  useControllableState,
  useMergeRefs,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { FloatingFocusManager } from '@floating-ui/react-dom-interactions';
import ProgressSpinner from '../ProgressSpinner/ProgressSpinner';
import SearchInput from '../SearchInput/SearchInput';
import FormLabel from '../Form/FormLabel';
import { AvatarProps } from '../Avatar/Avatar';
import { getDataAttributes } from '../../utils/utils';
import Tag from '../Tag/Tag';
import Box from '../Box/Box';
import IconButton from '../IconButton/IconButton';
import { DropdownEventArgs, DropdownProvider, useDropdownContext, useDropdownStyles } from './Dropdown.context';
import { DropdownDetailedOption, DropdownGroup, DropdownOption, DropdownOptionProps } from './DropdownOption';
import DropdownButton from './DropdownButton';
import useFloatingDropdown from './hooks/useFloatingDropdown';
import { NoResultsFound, useSimpleSearch } from './hooks/useSimpleSearch';
import { isSearchable } from './isNodeMatch';
import { DropdownProps } from './DropdownProps';

const MultiSelectContext = createContext(false);

type DropdownSearchCustomProps = {
  value: string;
  onChange: (newValue: string) => void;
};
type DropdownSearchProps = { placeholder?: string; reset?: boolean };
const DropdownSearch = ({
  placeholder = 'Start typing to filter options',
  reset = true,
  ...rest
}: DropdownSearchProps | (DropdownSearchProps & DropdownSearchCustomProps)) => {
  const { search } = useDropdownStyles();
  const { searchOnChange, searchOnSubmit, searchRef, searchValue } = useDropdownContext();

  const { onChange, value } = 'onChange' in rest ? rest : { onChange: searchOnChange, value: searchValue };

  const onChangeCB = useCallback(
    (q: string) => {
      onChange(q);
    },
    [onChange],
  );
  useEffect(() => {
    return () => {
      if (reset) {
        onChange('');
      }
    };
  }, [reset]);
  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        searchOnSubmit();
      }
    },
    [searchOnSubmit],
  );
  return (
    <SearchInput
      aria-label={placeholder}
      inputRef={searchRef}
      onChange={onChangeCB}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      role="search"
      sx={search}
      value={value}
    />
  );
};
export { DropdownOption, DropdownGroup, DropdownSearch, NoResultsFound, DropdownDetailedOption };

function useOptionListWithIndexes({ children }: { children: ReactNode }) {
  return useMemo(() => {
    const childList = React.Children.toArray(children);
    let idx = 0;
    const transform = (ch: ReactNode): ReactNode => {
      if (React.isValidElement(ch)) {
        if (ch.type === DropdownOption || ch.type === DropdownDetailedOption) {
          const index = idx;
          idx += 1;
          return cloneElement(ch, {
            ...ch.props,
            index,
          });
        }
        if ('children' in ch.props) {
          return cloneElement(ch, {
            ...ch.props,
            children: React.Children.toArray(ch.props.children).map(transform),
          });
        }
      }
      return ch;
    };
    return childList.map(transform);
  }, [children]);
}

type UseDropdownProps = {
  ref: React.Ref<Element>;
  optionsRef: React.RefObject<HTMLDivElement>;
};

function findOption<T>(
  children: ReactNode,
  value: T,
): { label: ReactNode; index: number; avatar?: AvatarProps } | null {
  const list = React.Children.toArray(children);
  for (let i = 0; i < list.length; i++) {
    const elem = list[i];
    if (React.isValidElement(elem) && !elem.props.isDisabled) {
      const optValue = typeof elem.props.value === 'undefined' ? null : elem.props.value;
      if (elem.type === DropdownOption && optValue === value) {
        return { index: elem.props.index, label: elem.props.children, avatar: elem.props.avatar };
      }
      const ch =
        findOption(elem.props.children, value) || (isSearchable(elem.type) && findOption(elem.type(elem.props), value));
      if (ch) {
        return ch;
      }
    }
  }
  return null;
}

function useDropdown<T>({
  children,
  defaultValue,
  disabled,
  dropdownWidth = 'match',
  name,
  onChange,
  optionsRef,
  placement = 'bottom-start',
  readOnly,
  ref,
  size,
  value,
  ...rest
}: DropdownProps<T> & UseDropdownProps) {
  const searchRef = useRef(null);
  const {
    activeIndex,
    close,
    context: floatingContext,
    floatingProps,
    getItemProps,
    getReferenceProps,
    isOpen,
    listRef,
    setActiveIndex,
    setSelectedIndex,
  } = useFloatingDropdown({ dropdownWidth, enabled: !readOnly, optionsRef, placement });

  const isMultiSelect = useContext(MultiSelectContext);

  const [formValue, setFormValue] = useControllableState<T>({
    defaultValue: isMultiSelect ? defaultValue || ([] as T) : defaultValue,
    onChange: (newValue) => onChange?.({ target: { name, value: newValue } }),
    value,
  });
  const [formLabel, setFormLabel] = useState<ReactNode>();
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarProps | undefined>();
  const refdChildren = useOptionListWithIndexes({ children });

  const searchOnSubmit = useCallback(() => {
    if (activeIndex !== null) {
      listRef.current[activeIndex]?.click();
    }
  }, [activeIndex]);
  const referenceKeyDown = useCallback(
    (ev: React.KeyboardEvent) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        searchOnSubmit();
      }
    },
    [searchOnSubmit],
  );
  const { searchOnChange, searchValue, ...searchResults } = useSimpleSearch({
    children: refdChildren,
    onSearch: () => setActiveIndex(null),
  });

  const onOptionSelected = useCallback(
    (args: DropdownEventArgs<T>) => {
      setFormValue((previous) => {
        if (Array.isArray(previous)) {
          if (previous.includes(args.value)) {
            return previous.filter((aPrevious) => aPrevious !== args.value) as T;
          }

          return [...previous, args.value] as T;
        }

        return args.value;
      });

      if (!isMultiSelect) {
        close();
      }
    },
    [close, isMultiSelect, setFormValue],
  );
  const context = useMemo(
    () => ({
      activeIndex,
      formValue,
      getItemProps,
      isMultiSelect,
      listRef,
      onOptionSelected,
      searchOnChange,
      searchOnSubmit,
      searchRef,
      searchValue,
      size,
    }),
    [
      listRef,
      formValue,
      onOptionSelected,
      activeIndex,
      getItemProps,
      searchOnChange,
      searchValue,
      searchRef,
      searchOnSubmit,
      size,
    ],
  );

  useEffect(() => {
    if (Array.isArray(formValue)) {
      setFormLabel(
        formValue.length > 0 ? (
          <Box alignItems="center" display="flex" gap={8}>
            <Box display="flex" flexGrow={1} flexWrap="wrap" gap={8}>
              {formValue
                ?.sort((a, b) => {
                  if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                  }

                  if (a === null) {
                    return -1;
                  }

                  return 1;
                })
                .map((formValueItem) => {
                  if (typeof formValueItem !== 'string' && formValueItem !== null) {
                    return <>{formValueItem}</>;
                  }

                  const currentOption = findOption(refdChildren, formValueItem);

                  return (
                    <Tag
                      colorScheme="neutral"
                      isDisabled={disabled}
                      key={formValueItem}
                      onClose={(event) => {
                        event.stopPropagation();

                        setFormValue((previous: T) => {
                          if (!Array.isArray(previous)) {
                            return previous;
                          }

                          return previous.filter((aPrevious) => aPrevious !== formValueItem) as T;
                        });
                      }}
                      size="sm"
                    >
                      {currentOption?.label || formValueItem}
                    </Tag>
                  );
                })}
            </Box>
            <IconButton
              aria-label="Clear all"
              as="span"
              color="icon/secondary"
              iconName="Cross"
              iconSize="24"
              isDisabled={disabled}
              onClick={(event) => {
                event.stopPropagation();

                setFormValue([] as T);
              }}
              size="sm"
              variant="tertiary"
            />
          </Box>
        ) : null,
      );
      setSelectedIndex(null);
      setSelectedAvatar(undefined);
    } else {
      const currentOption = findOption(refdChildren, formValue);
      if (currentOption) {
        setFormLabel(currentOption.label);
        setSelectedIndex(currentOption.index);
        setSelectedAvatar(currentOption.avatar);
      } else {
        setFormLabel(null);
        setSelectedIndex(null);
        setSelectedAvatar(undefined);
      }
    }
  }, [refdChildren, formValue]);
  return {
    avatar: selectedAvatar,
    context,
    floatingContext,
    floatingProps,
    formLabel,
    formValue,
    isOpen,
    referenceProps: getReferenceProps({
      onKeyDown: referenceKeyDown,
      ref,
    }),
    searchOnSubmit,
    searchRef,
    ...searchResults,
    ...rest,
  };
}

const Dropdown = forwardRef<Element, DropdownProps<string | null>>(
  (
    {
      'aria-label': ariaLabel,
      badge,
      buttonProps = {},
      defaultValue,
      disabled,
      dropdownMaxHeight,
      dropdownMinHeight,
      dropdownWidth,
      errorText,
      formLabel: customFormLabel,
      helperText,
      iconName,
      infoTooltipLabel,
      infoTooltipProps,
      isError,
      isWarning,
      label,
      name,
      onBlur,
      onChange,
      onScrolledToBottom,
      placeholder,
      placement,
      readOnly,
      required,
      returnFocus = true,
      search,
      size = 'lg',
      value,
      ...props
    },
    ref,
  ) => {
    const dataAttributes = getDataAttributes(props);

    const optionsRef = useRef(null);
    const {
      avatar,
      children,
      context: dropdownCtx,
      floatingContext,
      floatingProps,
      formLabel,
      formValue,
      isOpen,
      referenceProps,
      searchRef,
      ...rest
    } = useDropdown({
      defaultValue,
      disabled,
      dropdownWidth,
      isWarning,
      name,
      onChange,
      optionsRef,
      placement,
      readOnly,
      ref,
      size,
      value,
      ...props,
    });

    const dropdownStyles = useMultiStyleConfig('Dropdown', {
      hasAvatar: Boolean(avatar),
      disabled,
      isError,
      isMultiSelect: useContext(MultiSelectContext),
      isOpen,
      isWarning,
      placeholder: !formLabel,
      readOnly,
      size,
      ...rest,
    });

    const blurHandler = () => {
      if (!onBlur) {
        return;
      }
      if (!isOpen) {
        onBlur({ target: { name, value: formValue } });
      }
    };
    const listStyles = useMemo(
      () => ({
        ...dropdownStyles.list,
        '--dropdown-floating-max': dropdownMaxHeight,
        '--dropdown-floating-min': dropdownMinHeight,
      }),
      [dropdownMinHeight, dropdownMaxHeight, dropdownStyles.list],
    );
    const searchElement = search === false ? null : search || <DropdownSearch />;
    const buttonId = useId();

    const onScrolledToBottomRef = useRef(onScrolledToBottom);

    useEffect(() => {
      onScrolledToBottomRef.current = onScrolledToBottom;
    }, [onScrolledToBottom]);

    const internalRef = useCallback((node: HTMLDivElement | null) => {
      if (!node) return;

      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = node;
        if (scrollTop + clientHeight >= scrollHeight) {
          onScrolledToBottomRef.current?.();
        }
      };

      node.addEventListener('scroll', handleScroll);

      // Cleanup when the element is unmounted or ref changes
      return () => node.removeEventListener('scroll', handleScroll);
    }, []);

    const mergedRef = useMergeRefs(internalRef, optionsRef);

    return (
      <>
        {name && formValue && <input name={name} type="hidden" value={formValue} />}
        <DropdownProvider context={dropdownCtx} styles={dropdownStyles}>
          <FormControl {...props} {...dataAttributes} isDisabled={disabled} isInvalid={isError} isRequired={required}>
            <FormLabel
              badge={badge}
              infoTooltipLabel={infoTooltipLabel}
              infoTooltipProps={infoTooltipProps}
              htmlFor={buttonId}
            >
              {label}
            </FormLabel>
            <DropdownButton
              {...referenceProps}
              {...buttonProps}
              aria-label={ariaLabel}
              avatar={avatar}
              id={buttonId}
              iconName={iconName}
              blurHandler={blurHandler}
              disabled={disabled}
              formLabel={customFormLabel ?? formLabel}
              placeholder={placeholder}
              readOnly={readOnly}
              isError={isError}
              isOpen={isOpen}
              isWarning={isWarning}
              size={size}
            />
            {errorText && <FormErrorMessage as="p">{errorText}</FormErrorMessage>}
            {!errorText && helperText && (
              <FormHelperText data-disabled={disabled || undefined} as="p">
                {helperText}
              </FormHelperText>
            )}
          </FormControl>
          {isOpen && (
            <FloatingFocusManager context={floatingContext} initialFocus={searchRef} returnFocus={returnFocus}>
              <chakra.div {...floatingProps} {...dataAttributes} sx={listStyles}>
                {searchElement}
                <chakra.div ref={mergedRef} __css={dropdownStyles.options} tabIndex={-1}>
                  {children}
                  {onScrolledToBottom && (
                    <chakra.div display="flex" justifyContent="space-around">
                      <ProgressSpinner />
                    </chakra.div>
                  )}
                </chakra.div>
              </chakra.div>
            </FloatingFocusManager>
          )}
        </DropdownProvider>
      </>
    );
  },
);

export function typedDropdown<T>() {
  return {
    Dropdown: Dropdown as React.ForwardRefExoticComponent<DropdownProps<T> & React.RefAttributes<Element>>,
    DropdownOption: DropdownOption as (p: DropdownOptionProps<T>) => JSX.Element,
  };
}

export const MultiSelectDropdown = (props: DropdownProps<(string | null)[]>) => {
  const { Dropdown: TypedDropdown } = typedDropdown<(string | null)[]>();

  const { children, ...rest } = props;

  return (
    <MultiSelectContext.Provider value>
      <TypedDropdown {...rest}>{children}</TypedDropdown>
    </MultiSelectContext.Provider>
  );
};

export default Dropdown;
